import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import LoadingButton from '../../../../components/buttons/Button';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TimeAgo from '../../../../components/utils/TimeAgo';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import TemplateDiffModal from './TemplateDiffModal';

import {
  GetTemplateSyncTargets,
  GetLocalTemplateManifest,
  GetTemplateSyncOverview,
  GetTemplateSyncDetail,
  CompareTemplateSync,
  PushTemplateSync,
  PullTemplateSync
} from '../../../../redux/actions/business/notifications/EmailTemplateSyncActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';
const CUSTOM = '__custom__';

const STATUS = {
  missing_on_target: { label: 'Not on target', color: 'warning', accent: TILE_ACCENT.warn },
  different:         { label: 'Content differs', color: 'error', accent: TILE_ACCENT.bad },
  in_sync:           { label: 'In sync', color: 'success', accent: TILE_ACCENT.good },
  only_on_target:    { label: 'Only here on target', color: 'info', accent: TILE_ACCENT.info },
};

const shortHash = (h) => (h ? h.slice(0, 8) : null);

const EmailTemplateSyncPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pulling, setPulling] = useState(false);

  const [config, setConfig] = useState(null);
  const [local, setLocal] = useState(null);
  const [overview, setOverview] = useState(null);

  const [target, setTarget] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [comparison, setComparison] = useState(null);
  const [selected, setSelected] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("push");
  // Which codes the pending confirmation applies to. Set for a single-row
  // action; null means "whatever is selected".
  const [pendingCodes, setPendingCodes] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    handleLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The environment argument every request needs: either a configured key or an
  // ad-hoc URL, never both.
  const targetArgs = () => ({
    target: target === CUSTOM ? "" : target,
    url: target === CUSTOM ? customUrl.trim() : "",
  });

  async function handleLoad() {
    setLoading(true);
    // All three run unconditionally. This screen must be useful on arrival with
    // nothing configured, so it always shows this environment's own templates
    // and whatever else it can reach.
    const [cfg, manifest, ov] = await Promise.all([
      dispatch(GetTemplateSyncTargets(() => {})),
      dispatch(GetLocalTemplateManifest(() => {})),
      dispatch(GetTemplateSyncOverview(() => {})),
    ]);
    setConfig(cfg);
    setLocal(manifest);
    setOverview(ov);
    if (cfg?.targets?.length) setTarget((prev) => prev || cfg.targets[0].key);
    setLoading(false);
  }

  async function handleCompare() {
    if (!canCompare) return;
    setComparison(null);
    setSelected([]);
    const { target: key, url } = targetArgs();
    setComparison(await dispatch(CompareTemplateSync(key, url, setComparing)));
  }

  // After anything that writes, both sides have moved — re-read rather than
  // patching the table, so what is on screen is what is in the databases.
  async function refreshAfterTransfer() {
    setLocal(await dispatch(GetLocalTemplateManifest(() => {})));
    dispatch(GetTemplateSyncOverview(() => {})).then(setOverview);
    await handleCompare();
  }

  async function handlePush(codes) {
    setConfirmOpen(false);
    const list = codes || pushableSelected;
    if (list.length === 0) return;
    const result = await dispatch(PushTemplateSync({ ...targetArgs(), codes: list }, setPushing));
    if (result) { setDetailOpen(false); await refreshAfterTransfer(); }
  }

  async function handlePull(codes) {
    setConfirmOpen(false);
    const list = codes || pullableSelected;
    if (list.length === 0) return;
    const result = await dispatch(PullTemplateSync({ ...targetArgs(), codes: list }, setPulling));
    if (result) { setDetailOpen(false); await refreshAfterTransfer(); }
  }

  // Every transfer started from the table is confirmed first. The diff modal is
  // the one exception: you have just read both sides there, so its buttons act
  // directly.
  function askTransfer(mode, codes) {
    setConfirmMode(mode);
    setPendingCodes(codes || null);
    setConfirmOpen(true);
  }

  async function handleOpenDetail(code) {
    if (!code) return;
    setDetail(null);
    setDetailOpen(true);
    const { target: key, url } = targetArgs();
    setDetail(await dispatch(GetTemplateSyncDetail(code, key, url, setDetailLoading)));
  }

  // Before a comparison the table lists this environment's templates; afterwards
  // it shows the merged diff. Either way the page always has content.
  const rows = comparison?.rows
    || (local?.templates || []).map((t) => ({ code: t.code, status: null, local: t, remote: null }));

  const blocked = comparison?.blocked
    || (local?.templates || []).filter((t) => !t.code).map((t) => ({ id: t.id, name: t.name, tag: t.tag }));

  const summary = comparison?.summary;

  // Direction is decided per row by which side actually holds the template.
  const canPushRow = (row) => !!row.code && !!row.local;
  const canPullRow = (row) => !!row.code && !!row.remote;

  const selectable = rows.filter((r) => r.code && (r.local || r.remote));
  const pushableSelected = rows.filter((r) => selected.includes(r.code) && canPushRow(r)).map((r) => r.code);
  const pullableSelected = rows.filter((r) => selected.includes(r.code) && canPullRow(r)).map((r) => r.code);

  const toggle = (code) => setSelected((prev) =>
    prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  const toggleAll = () => setSelected((prev) =>
    prev.length === selectable.length ? [] : selectable.map((r) => r.code));

  const selectedTargetConfig = (config?.targets || []).find((t) => t.key === target);
  const targetLabel = target === CUSTOM
    ? (customUrl.trim() || "the other environment")
    : (comparison?.target?.label || selectedTargetConfig?.label || "the target");

  const canCompare = !!target && (target !== CUSTOM || !!customUrl.trim());

  const confirmCodes = pendingCodes || (confirmMode === "push" ? pushableSelected : pullableSelected);
  const confirmCount = confirmCodes.length;

  return (
    <Box>
      <ConfirmationModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={
          confirmMode === "push"
            ? `Push ${confirmCount} template${confirmCount === 1 ? "" : "s"} to ${targetLabel}?`
            : `Pull ${confirmCount} template${confirmCount === 1 ? "" : "s"} into this environment?`
        }
        message={
          confirmMode === "push"
            ? `Matched by code and overwritten on ${targetLabel}. Nothing is deleted there, and templates you did not select are untouched.`
            : `Matched by code and overwritten HERE with the copy from ${targetLabel}. Nothing is deleted, and templates you did not select are untouched.`
        }
        onConfirm={() => (confirmMode === "push" ? handlePush(confirmCodes) : handlePull(confirmCodes))}
        />

      <TemplateDiffModal
        open={detailOpen}
        setOpen={setDetailOpen}
        loading={detailLoading}
        detail={detail}
        targetLabel={targetLabel}
        pushing={pushing}
        pulling={pulling}
        onPush={() => handlePush([detail?.code])}
        onPull={() => handlePull([detail?.code])}
        />

      <TopBar
        title={"Email Template Sync"}
        button1Text={"Open template list"}
        button1OnClick={() => navigate("/business/notifications/email-templates")}
        button2Text={"Refresh"}
        button2OnClick={() => handleLoad()}
        />

      <Typography
        variant='caption'
        sx={{ color: LABEL_COLOR, display: "block", mb: "16px" }}>
        Templates are matched across environments by their <b>code</b>, never by id.
        Comparing sends only fingerprints; a body is transferred only when you push or
        pull. Neither direction ever deletes anything.
      </Typography>

      {
        loading
        ?
        <Box height={"240px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        <Box>
          {/* ---------- how many templates each environment holds ---------- */}
          {
            (overview?.environments || []).length > 0
            &&
            <Box display={"flex"} gap={"12px"} flexWrap={"wrap"} mb={"18px"}>
              {
                overview.environments.map((env) => (
                  <Box
                    key={env.key}
                    sx={{
                      minWidth: "190px",
                      border: `1px solid ${env.reachable ? "#2c2d2f" : `${TILE_ACCENT.bad}80`}`,
                      background: env.reachable ? "rgba(0,0,0,0.2)" : `${TILE_ACCENT.bad}14`,
                      borderRadius: "12px",
                      padding: "12px 14px",
                    }}>
                    <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                      {env.label}
                    </Typography>
                    {
                      env.reachable
                      ?
                      <Box>
                        <Typography variant='h6'>{env.total}</Typography>
                        <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                          template{env.total === 1 ? "" : "s"}
                          {env.without_code ? ` · ${env.without_code} without a code` : ""}
                        </Typography>
                      </Box>
                      :
                      <Box>
                        <Typography variant='body2' sx={{ color: TILE_ACCENT.bad }}>Unreachable</Typography>
                        <Tooltip title={env.error || ""}>
                          <Typography variant='caption'
                            sx={{ color: LABEL_COLOR, display: "block",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  maxWidth: "190px" }}>
                            {env.error}
                          </Typography>
                        </Tooltip>
                      </Box>
                    }
                  </Box>
                ))
              }
            </Box>
          }

          {/* ---------- environment + target ---------- */}
          <Box className='flexCenter_Row' gap={"14px"} flexWrap={"wrap"} mb={"4px"}>
            <Box>
              <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                This environment
              </Typography>
              <Chip size='small' color='primary' variant='filled'
                label={config?.environment || local?.environment || "unknown"} />
            </Box>

            <TextField
              color='primary'
              select size='small' label='Compare with' sx={{ minWidth: "260px" }}
              value={target}
              onChange={(e) => { setTarget(e.target.value); setComparison(null); setSelected([]); }}>
              {(config?.targets || []).map((t) => (
                <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
              ))}
              <MenuItem value={CUSTOM}>Another environment (enter URL)…</MenuItem>
            </TextField>

            {
              target === CUSTOM
              &&
              <TextField
                color='primary'
                size='small' label='Environment URL' sx={{ minWidth: "380px" }}
                placeholder='https://alpha-api.lusid.ai/us/identity'
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                />
            }

            <LoadingButton
              variant='contained'
              loading={comparing}
              disabled={!canCompare}
              onClick={() => handleCompare()}>
              Compare
            </LoadingButton>
          </Box>

          <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "16px" }}>
            {
              target === CUSTOM
                ? "Point at any environment's identity base URL — nothing needs to be pre-configured."
                : (selectedTargetConfig?.url || "")
            }
          </Typography>

          {/* ---------- bulk transfer, both directions ---------- */}
          {
            selected.length > 0
            &&
            <Box className='flexCenter_Row' gap={"12px"} flexWrap={"wrap"} mb={"16px"}
              sx={{ border: "1px solid #2c2d2f", background: "rgba(0,0,0,0.2)",
                    borderRadius: "12px", padding: "12px 16px" }}>
              <Typography variant='body2'>
                <b>{selected.length}</b> selected
              </Typography>

              <LoadingButton
                variant='contained'
                loading={pushing}
                disabled={pushableSelected.length === 0}
                endIcon={<ArrowForwardIcon />}
                onClick={() => askTransfer("push")}>
                Push {pushableSelected.length} to {targetLabel}
              </LoadingButton>

              <LoadingButton
                variant='contained'
                loading={pulling}
                disabled={pullableSelected.length === 0}
                startIcon={<ArrowBackIcon />}
                onClick={() => askTransfer("pull")}>
                Pull {pullableSelected.length} here
              </LoadingButton>

              <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                {
                  pullableSelected.length === 0
                    ? "Pull becomes available for templates that exist on the target — compare first."
                    : "Push sends this environment's copy outward; pull brings the target's copy in."
                }
              </Typography>
            </Box>
          }

          {
            config?.auth_bypassed
            &&
            <Box
              sx={{ border: `1px solid ${TILE_ACCENT.bad}80`, background: `${TILE_ACCENT.bad}20`,
                    borderRadius: "12px", padding: "12px 16px", mb: "16px" }}>
              <Box className='flexCenter_Row' gap={"10px"}>
                <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.bad }} />
                <Typography variant='body2'>
                  <b>Sync authentication is switched off</b> — set{" "}
                  <b>BYPASS_EMAIL_SYNC_SECRET = false</b> in{" "}
                  <b>src/common/sync_secret_check.js</b> before this build goes anywhere public.
                </Typography>
              </Box>
            </Box>
          }

          {/* ---------- templates that cannot participate ---------- */}
          {
            blocked.length > 0
            &&
            <Box
              sx={{ border: `1px solid ${TILE_ACCENT.bad}40`, background: `${TILE_ACCENT.bad}14`,
                    borderRadius: "12px", padding: "12px 16px", mb: "16px" }}>
              <Box className='flexCenter_Row' gap={"10px"} mb={"6px"}>
                <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.bad }} />
                <Typography variant='body2'>
                  <b>{blocked.length}</b> template{blocked.length === 1 ? " has" : "s have"} no code,
                  so {blocked.length === 1 ? "it" : "they"} cannot be synced
                </Typography>
              </Box>
              <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "6px" }}>
                The code is what ties a template to its counterpart in another environment.
                Give each one a code on the template list, then compare again.
              </Typography>
              {
                blocked.map((b, i) => (
                  <Typography key={i} variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                    id {b.id} · {b.name || "(unnamed)"}{b.tag ? ` · ${b.tag}` : ""}
                  </Typography>
                ))
              }
            </Box>
          }

          {
            summary?.duplicate_codes?.length > 0
            &&
            <Box
              sx={{ border: `1px solid ${TILE_ACCENT.bad}40`, background: `${TILE_ACCENT.bad}14`,
                    borderRadius: "12px", padding: "12px 16px", mb: "16px" }}>
              <Typography variant='body2'>
                Duplicate codes here: {summary.duplicate_codes.join(", ")} — a code must be unique
                or the wrong template will be overwritten.
              </Typography>
            </Box>
          }

          {/* ---------- totals, once compared ---------- */}
          {
            comparison
            &&
            <Box className='flexCenter_Row' gap={"16px"} flexWrap={"wrap"} mb={"12px"}>
              <Typography variant='body2'>
                <b>{comparison.source.total}</b> here ({comparison.source.environment})
              </Typography>
              <CloudSyncOutlinedIcon sx={{ color: LABEL_COLOR }} />
              <Typography variant='body2'>
                <b>{comparison.target.total}</b> on {comparison.target.label} ({comparison.target.environment})
              </Typography>
              <Chip size='small' variant='outlined' color='success' label={`${summary.in_sync} in sync`} />
              {summary.different > 0 && <Chip size='small' variant='outlined' color='error' label={`${summary.different} differ`} />}
              {summary.missing_on_target > 0 && <Chip size='small' variant='outlined' color='warning' label={`${summary.missing_on_target} not on target`} />}
              {summary.only_on_target > 0 && <Chip size='small' variant='outlined' color='info' label={`${summary.only_on_target} only on target`} />}
            </Box>
          }

          {/* ---------- the table ---------- */}
          {
            comparing
            ?
            <Box height={"200px"} className="flexCenterCenterRow"><CircularProgress /></Box>
            :
            rows.length === 0
            ?
            <Box height={"180px"} className="flexCenterCenterRow">
              <Typography>No email templates in this environment</Typography>
            </Box>
            :
            <Table className="block">
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      disabled={selectable.length === 0}
                      checked={selectable.length > 0 && selected.length === selectable.length}
                      indeterminate={selected.length > 0 && selected.length < selectable.length}
                      onChange={toggleAll} />
                  </TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Here</TableCell>
                  <TableCell>{comparison ? comparison.target.label : "Target"}</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Transfer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  rows.map((row, index) => {
                    const meta = STATUS[row.status] || {};
                    return (
                      <TableRow
                        key={row.code || `nocode-${index}`}
                        sx={{ borderLeft: `3px solid ${meta.accent || 'transparent'}` }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            disabled={!row.code || (!row.local && !row.remote)}
                            checked={selected.includes(row.code)}
                            onChange={() => toggle(row.code)} />
                        </TableCell>
                        <TableCell>
                          {
                            row.code
                            ?
                            <Typography variant='body2' sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                              <Copyable>{row.code}</Copyable>
                            </Typography>
                            :
                            <Chip size='small' label="no code" color='warning' variant='outlined' />
                          }
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {row.local?.name || row.remote?.name || <NA />}
                          </Typography>
                          <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                            {row.local?.tag || row.remote?.tag || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {
                            row.local
                            ?
                            <Box>
                              <Tooltip title="Content fingerprint">
                                <Typography variant='caption' sx={{ fontFamily: "monospace" }}>
                                  {shortHash(row.local.content_hash)}
                                </Typography>
                              </Tooltip>
                              <TimeAgo value={row.local.updatedAt} color={LABEL_COLOR} />
                            </Box>
                            : <Typography variant='caption' sx={{ color: LABEL_COLOR }}>not here</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {
                            row.remote
                            ?
                            <Box>
                              <Tooltip title="Content fingerprint">
                                <Typography variant='caption' sx={{ fontFamily: "monospace" }}>
                                  {shortHash(row.remote.content_hash)}
                                </Typography>
                              </Tooltip>
                              <TimeAgo value={row.remote.updatedAt} color={LABEL_COLOR} />
                            </Box>
                            : <Typography variant='caption' sx={{ color: LABEL_COLOR }}>—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {
                            row.status
                            ? <Chip size='small' variant='filled' color={meta.color || 'default'}
                                label={meta.label || row.status} />
                            : <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                                not compared
                              </Typography>
                          }
                        </TableCell>
                        <TableCell align='right'>
                          <Box className='flexCenterFERow' gap={"4px"}>
                            <Tooltip title={canCompare ? "View both sides" : "Choose an environment first"}>
                              <span>
                                <LoadingButton
                                  size='small' variant='outlined'
                                  disabled={!row.code || !canCompare}
                                  onClick={() => handleOpenDetail(row.code)}>
                                  <VisibilityOutlinedIcon fontSize='small' />
                                </LoadingButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={canPushRow(row) ? `Push to ${targetLabel}` : "Nothing here to send"}>
                              <span>
                                <LoadingButton
                                  size='small' variant='outlined'
                                  disabled={!canPushRow(row) || !canCompare}
                                  onClick={() => askTransfer("push", [row.code])}>
                                  <ArrowForwardIcon fontSize='small' />
                                </LoadingButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={canPullRow(row) ? `Pull from ${targetLabel} into this environment` : "Compare first — nothing known on the target"}>
                              <span>
                                <LoadingButton
                                  size='small' variant='outlined'
                                  disabled={!canPullRow(row) || !canCompare}
                                  onClick={() => askTransfer("pull", [row.code])}>
                                  <ArrowBackIcon fontSize='small' />
                                </LoadingButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          }

          {
            comparison && rows.length > 0 && summary?.in_sync === rows.length && blocked.length === 0
            &&
            <Box className='flexCenter_Row' gap={"10px"} mt={"14px"}>
              <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
              <Typography variant='body2'>
                Every coded template matches {comparison.target.label}. Nothing to transfer.
              </Typography>
            </Box>
          }
        </Box>
      }
    </Box>
  )
}

export default EmailTemplateSyncPage;
